"use client";

import { Suspense, useState } from "react";
import { signIn } from "@/lib/auth-client";
import posthog from "posthog-js";
import { AlertCircle, ArrowLeft, Mail } from "lucide-react";
import { toast } from "sonner";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { SignInErrorAlert } from "./sign-in-error-alert";

export default function LoginPage() {
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [emailFormOpen, setEmailFormOpen] = useState(false);

  const handleMicrosoftSignIn = async () => {
    setLoading("microsoft");
    setError(null);
    posthog.capture("microsoft_signin_clicked");
    try {
      await signIn.social({
        provider: "microsoft",
        callbackURL: "/",
        // Without this, failures land on /api/auth/error, which bounces
        // through / back to a blank login page with no explanation.
        errorCallbackURL: "/login",
      });
    } catch (err) {
      setError("Couldn't sign in with Microsoft. Try again.");
      toast.error("Couldn't sign in with Microsoft", {
        description: "Try again.",
      });
      posthog.captureException(err);
      setLoading(null);
    }
  };

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading("magic-link");
    setError(null);
    try {
      const { error: err } = await signIn.magicLink({
        email: email.trim(),
        callbackURL: "/",
        errorCallbackURL: "/login",
      });
      if (err) {
        const description =
          err.message ?? "Couldn't send the sign-in link. Try again.";
        setError(description);
        toast.error("Couldn't send the sign-in link", { description });
        setLoading(null);
      } else {
        posthog.capture("magic_link_requested");
        posthog.identify(email.trim());
        setMagicLinkSent(true);
        setLoading(null);
      }
    } catch (err) {
      setError("Couldn't send the sign-in link. Try again.");
      toast.error("Couldn't send the sign-in link", {
        description: "Try again.",
      });
      posthog.captureException(err);
      setLoading(null);
    }
  };

  if (magicLinkSent) {
    return (
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <h1 className="text-title">Check your email</h1>
          <p className="text-body text-secondary-foreground">
            We sent a sign-in link to{" "}
            <span className="text-foreground font-medium break-all">
              {email}
            </span>
            .
          </p>
          <p className="text-compact text-muted-foreground">
            The link expires in 5 minutes. Check your spam folder if you
            don&apos;t see it.
          </p>
        </div>
        <Button
          variant="outline"
          size="lg"
          onClick={() => {
            setMagicLinkSent(false);
            setEmail("");
          }}
          className="h-12 w-full"
        >
          <ArrowLeft />
          Use a different email
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <h1 className="text-title">Sign in</h1>
        <p className="text-body text-secondary-foreground">
          Use your Microsoft work account.
        </p>
      </div>

      {error ? (
        <Alert variant="destructive">
          <AlertCircle />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : (
        // useSearchParams needs a Suspense boundary to keep this page static.
        <Suspense fallback={null}>
          <SignInErrorAlert />
        </Suspense>
      )}

      <div className="flex flex-col gap-3">
        {/* Microsoft SSO: the primary sign-in path. */}
        <Button
          size="lg"
          onClick={handleMicrosoftSignIn}
          disabled={!!loading}
          autoFocus
          className="h-12 w-full"
        >
          {loading === "microsoft" ? (
            <Spinner />
          ) : (
            <>
              <svg viewBox="0 0 21 21" className="h-4 w-4" fill="none">
                <rect x="1" y="1" width="9" height="9" fill="#F25022" />
                <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
                <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
                <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
              </svg>
              Continue with Microsoft
            </>
          )}
        </Button>

        {/* Magic link: the fallback for people without Microsoft access. The
         * secondary button opens the form in place. */}
        {emailFormOpen ? (
          <form onSubmit={handleMagicLink} className="flex flex-col gap-3 pt-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="email" className="text-label">
                Work email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@johnstonsgrain.com"
                autoComplete="email"
                autoFocus
                required
                className="h-12"
              />
            </div>
            <Button
              type="submit"
              variant="outline"
              size="lg"
              disabled={!!loading || !email.trim()}
              className="h-12 w-full"
            >
              {loading === "magic-link" ? (
                <Spinner />
              ) : (
                <>
                  <Mail />
                  Send sign-in link
                </>
              )}
            </Button>
          </form>
        ) : (
          <Button
            type="button"
            variant="outline"
            size="lg"
            onClick={() => {
              setError(null);
              setEmailFormOpen(true);
            }}
            disabled={!!loading}
            className="h-12 w-full"
          >
            <Mail />
            Email me a sign-in link
          </Button>
        )}
      </div>

      <p className="text-caption text-muted-foreground">
        No password needed. Without Microsoft, we email a link that signs you in
        once and expires in 5 minutes.
      </p>
    </div>
  );
}
